import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { ArrowRight, Download, FileText, Star, BookOpen, CheckCircle, ArrowLeft, Lightbulb, TrendingUp, Users, BarChart3 } from "lucide-react";
import CompanyLayout from "@/components/CompanyLayout";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import G2RecognitionSection from "@/components/G2RecognitionSection";
import whitepaperCover from "@/assets/whitepaper-manager-intelligence-cover.png";

const ManagerIntelligenceWhitepaper = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  // Load HubSpot form script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js-na2.hsforms.net/forms/embed/4567818.js';
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://js-na2.hsforms.net/forms/embed/4567818.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const keyInsights = [
    {
      icon: <Lightbulb className="h-5 w-5" />,
      title: "Beyond Annual Surveys",
      description: "Why point-in-time snapshots fail to capture the real pulse of your organization"
    },
    {
      icon: <TrendingUp className="h-5 w-5" />,
      title: "Real-Time Intelligence",
      description: "How continuous data streams enable proactive rather than reactive management"
    },
    {
      icon: <Users className="h-5 w-5" />,
      title: "Manager Empowerment",
      description: "Giving frontline leaders the insights they need to support their teams effectively"
    },
    {
      icon: <BarChart3 className="h-5 w-5" />,
      title: "Actionable Analytics",
      description: "Turning data into decisions that improve engagement and retention"
    }
  ];

  const tableOfContents = [
    "Executive Summary",
    "The Problem with Traditional Engagement Metrics",
    "What is Manager Intelligence?",
    "The Five Pillars of Real-Time People Data",
    "Case Studies: Organizations Leading the Way",
    "Implementation Roadmap",
    "Measuring Success: KPIs That Matter",
    "Conclusion & Next Steps"
  ];

  return (
    <CompanyLayout
      title="Manager Intelligence 2.0"
      description="The Real-Time Data Layer for People Leaders"
    >
      <div className="bg-background">
        {/* Hero Section */}
        <section className="pt-24 md:pt-32 pb-20 bg-gradient-to-b from-muted/50 to-background">
          <div className="container mx-auto px-4 max-w-7xl">
            {/* Breadcrumb */}
            <a 
              href="/resources/whitepapers" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-10 transition-colors text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Whitepapers
            </a>

            {/* Full-width headline */}
            <div className="mb-10">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight tracking-tight mb-6">
                Manager Intelligence 2.0: From Reactive to Proactive Talent Decisions
              </h1>
              
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium">
                <FileText className="h-4 w-4" />
                Free Whitepaper
              </div>
            </div>

            {/* Two-column layout for content and image */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-lg md:text-xl text-primary font-medium mb-6">
                  Why Recognition Data Should Be the First Input to Leadership, Succession, and Retention Strategies
                </p>
                
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-8">
                  Build better managers with better data. This white paper explains how continuous, emotionally intelligent recognition becomes the strongest real-time, bias-resistant signal of employee contribution, leadership readiness, and retention risk.
                </p>

                <div className="flex flex-wrap gap-4">
                  <Button 
                    size="lg"
                    onClick={() => document.getElementById('download-form')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Download Free PDF
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                    onClick={() => setIsDemoModalOpen(true)}
                  >
                    Book a Demo
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Whitepaper Cover Image */}
              <div className="flex justify-center lg:justify-end">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl blur-2xl transform scale-105"></div>
                  <img 
                    src={whitepaperCover} 
                    alt="Manager Intelligence 2.0 Whitepaper Cover" 
                    className="relative rounded-xl shadow-2xl max-w-[280px] md:max-w-[340px] w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What You'll Learn + Download Form - Combined Section */}
        <section id="download-form" className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* What You'll Learn - Left */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
                  What You'll Learn
                </h2>
                
                <div className="space-y-5">
                  {keyInsights.map((insight, idx) => (
                    <div 
                      key={idx}
                      className="flex gap-4"
                    >
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary flex-shrink-0">
                        {insight.icon}
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-foreground mb-1">{insight.title}</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">{insight.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Download Form - Right */}
              <div className="bg-card rounded-2xl shadow-lg border border-border p-8">
                {/* HubSpot Form Embed */}
                <div 
                  className="hs-form-frame" 
                  data-region="na2" 
                  data-form-id="8b28cdca-43bb-47f8-827d-24f7f6815ac1" 
                  data-portal-id="4567818"
                ></div>
                
                <p className="text-xs text-muted-foreground text-center pt-4">
                  We're committed to your privacy. See our{' '}
                  <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* G2 Recognition Section */}
        <G2RecognitionSection />
      </div>
      
      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen}
      />
    </CompanyLayout>
  );
};

export default ManagerIntelligenceWhitepaper;
